import { ref } from 'vue'

// 업로드 대상 로컬 파일이 일시적으로 확인되지 않을 때 재시도를 유도하는 에러
export class FileMissingError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'FileMissingError'
  }
}

// localStorage 키 (스키마 변경 없이 파일 부재 이력을 추적)
const STORAGE_KEY = 'photo_file_missing_counts_v1'
// 부재 확정 최소 확인 횟수 — 별도 재시도(20초+ 간격) 3회면 확정 (약 1~1.5분)
const CONFIRM_MIN_COUNT = 3
// 동일 job의 연속 재시도 burst를 1회로 집계하기 위한 최소 간격
// (SyncQueue retry sweep이 30초 주기이므로 그보다 짧게 설정해야 sweep 재시도가 집계됨)
const COUNT_MIN_INTERVAL_MS = 20 * 1000
// 미확정 엔트리 정리 기준 (7일) — 확정 엔트리는 사용자가 사진을 지울 때까지 유지
const ENTRY_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

interface MissingEntry {
  count: number
  firstAt: number
  lastAt: number
  // 부재 확정 시각 (있으면 '기기에서 삭제됨' 확정 상태)
  confirmedAt?: number
}

// 부재 확정된 사진 localStringId 집합 (UI 배지 표기용 반응형 상태)
export const confirmedMissingIds = ref<Set<string>>(new Set())

// 저장소 로드 (손상 시 빈 객체로 초기화 + 7일 경과 미확정 엔트리 prune)
function loadEntries(): Record<string, MissingEntry> {
  let entries: Record<string, MissingEntry> = {}
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw) entries = JSON.parse(raw) as Record<string, MissingEntry>
  } catch {
    entries = {}
  }

  const now = Date.now()
  for (const key of Object.keys(entries)) {
    const entry = entries[key]
    if (
      !entry ||
      typeof entry.firstAt !== 'number' ||
      (!entry.confirmedAt && now - entry.firstAt > ENTRY_MAX_AGE_MS)
    ) {
      delete entries[key]
    }
  }
  return entries
}

function saveEntries(entries: Record<string, MissingEntry>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries))
  } catch (error) {
    console.error('[PhotoGuard] persist failed:', error)
  }
}

// 반응형 확정 집합을 저장소 기준으로 갱신
function syncConfirmedIds(entries: Record<string, MissingEntry>): void {
  confirmedMissingIds.value = new Set(Object.keys(entries).filter(key => entries[key].confirmedAt))
}

// 모듈 로드 시 기존 확정 상태 복원
syncConfirmedIds(loadEntries())

// 부재 확정 여부 (UI 배지 및 업로드 큐 스킵 판단용)
export function isFileMissingConfirmed(localStringId: string): boolean {
  return confirmedMissingIds.value.has(localStringId)
}

/**
 * 파일 부재 1회를 기록하고 부재 확정 여부를 판단
 * 확정되어도 행은 삭제하지 않음 — UI에 '기기에서 삭제됨'으로 표기되고 업로드만 중단
 * @param localStringId 대상 사진의 localStringId
 * @param label 로그용 항목명 (예: '결함', '시설')
 * @returns 'confirmed'면 부재 확정 (업로드 중단), 'retry'면 재시도 유도
 */
export function registerFileMissing(localStringId: string, label: string): 'retry' | 'confirmed' {
  const now = Date.now()
  const entries = loadEntries()
  const entry = entries[localStringId]

  if (entry?.confirmedAt) return 'confirmed'

  if (!entry) {
    entries[localStringId] = { count: 1, firstAt: now, lastAt: now }
    saveEntries(entries)
    console.warn(`[PhotoGuard] missing #1 (${label}) id=${localStringId} -> retry`)
    return 'retry'
  }

  // 수 초 내 연속 재시도 burst는 1회로 집계
  if (now - entry.lastAt > COUNT_MIN_INTERVAL_MS) {
    entry.count++
  }
  entry.lastAt = now

  const elapsedMin = Math.round((now - entry.firstAt) / 60000)
  const isConfirmed = entry.count >= CONFIRM_MIN_COUNT

  if (isConfirmed) {
    entry.confirmedAt = now
  }
  saveEntries(entries)

  if (!isConfirmed) {
    console.warn(
      `[PhotoGuard] missing #${entry.count} (${label}) id=${localStringId} elapsed=${elapsedMin}min -> retry`,
    )
    return 'retry'
  }

  syncConfirmedIds(entries)
  console.error(
    `[PhotoGuard] confirmed missing #${entry.count} (${label}) id=${localStringId} elapsed=${elapsedMin}min -> stop uploading (row kept)`,
  )
  return 'confirmed'
}

// 네이티브 파일 접근 에러가 '파일 없음'인지 판별 (ENOENT 등)
export function isFileNotFoundError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error)
  return /ENOENT|no such file|does not exist|not exist|file not found/i.test(message)
}

// 파일 확인 성공 또는 사진 삭제 시 이력 제거 (확정 상태 포함)
export function clearFileMissingCount(localStringId: string): void {
  const entries = loadEntries()
  if (!entries[localStringId]) return
  const wasConfirmed = Boolean(entries[localStringId].confirmedAt)
  delete entries[localStringId]
  saveEntries(entries)
  if (wasConfirmed) syncConfirmedIds(entries)
  console.info(`[PhotoGuard] cleared id=${localStringId} (file found or photo removed)`)
}
